import { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { MapPin, Globe, Phone, Mail, ArrowLeft, Flag, Pencil, Facebook, Instagram, Linkedin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Reveal } from "@/components/Reveal";
import { TextField, AreaField, Honeypot } from "@/components/form";
import { usePageMeta } from "@/hooks/usePageMeta";
import { api, apiError, fileUrl } from "@/lib/api";

const AREA_LABELS = { local: "Local", online: "Online", nationwide: "Nationwide" };
const SOCIAL_ICONS = { facebook: Facebook, instagram: Instagram, linkedin: Linkedin };

function UpdateDialog({ id }) {
  const [f, setF] = useState({ contact_name: "", email: "", changes: "", hp_field: "" });
  const [open, setOpen] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post(`/businesses/${id}/update-request`, f); toast.success("Update request sent. Thank you."); setOpen(false); setF({ contact_name: "", email: "", changes: "", hp_field: "" }); }
    catch (err) { toast.error(apiError(err)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="rounded-full border-gold text-navy hover:bg-gold/10" data-testid="open-update-request"><Pencil className="mr-2 h-4 w-4" /> Request a Listing Update</Button>
      </DialogTrigger>
      <DialogContent className="bg-white">
        <DialogHeader><DialogTitle className="font-display text-2xl text-navy">Request a Listing Update</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid gap-4" data-testid="update-request-form">
          <Honeypot value={f.hp_field} onChange={(v) => setF({ ...f, hp_field: v })} />
          <TextField label="Your name" name="contact_name" required value={f.contact_name} onChange={(e) => setF({ ...f, contact_name: e.target.value })} />
          <TextField label="Email" name="email" type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
          <AreaField label="What should be updated?" name="changes" required value={f.changes} onChange={(e) => setF({ ...f, changes: e.target.value })} />
          <Button type="submit" className="rounded-full bg-emerald hover:bg-emerald-dark text-white" data-testid="update-request-submit">Send Request</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ReportDialog({ id }) {
  const [f, setF] = useState({ reason: "", reporter_email: "", hp_field: "" });
  const [open, setOpen] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post(`/businesses/${id}/report`, f); toast.success("Thank you for helping keep the directory accurate."); setOpen(false); setF({ reason: "", reporter_email: "", hp_field: "" }); }
    catch (err) { toast.error(apiError(err)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" className="text-muted-foreground hover:text-ruby" data-testid="open-report"><Flag className="mr-2 h-4 w-4" /> Report listing</Button>
      </DialogTrigger>
      <DialogContent className="bg-white">
        <DialogHeader><DialogTitle className="font-display text-2xl text-navy">Report this listing</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid gap-4" data-testid="report-form">
          <Honeypot value={f.hp_field} onChange={(v) => setF({ ...f, hp_field: v })} />
          <AreaField label="What's inaccurate or inappropriate?" name="reason" required value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} />
          <TextField label="Your email (optional)" name="reporter_email" type="email" value={f.reporter_email} onChange={(e) => setF({ ...f, reporter_email: e.target.value })} />
          <Button type="submit" className="rounded-full bg-navy hover:bg-navy-700 text-white" data-testid="report-submit">Submit Report</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function BusinessProfile() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [biz, setBiz] = useState(null);
  const [notFound, setNotFound] = useState(false);
  usePageMeta(biz ? biz.name.replace(/^\[DEMO\]\s*/, "") : "Business", biz?.description);

  useEffect(() => {
    const demo = searchParams.get("demo") === "true";
    api.get(`/businesses/${id}`, { params: demo ? { demo: true } : {} })
      .then((r) => setBiz(r.data))
      .catch(() => api.get(`/businesses/${id}`, { params: { demo: true } })
        .then((r) => setBiz(r.data)).catch(() => setNotFound(true)));
  }, [id, searchParams]);

  if (notFound) return (
    <div className="mx-auto max-w-3xl px-5 py-32 text-center">
      <h1 className="font-display text-4xl text-navy">Listing not found</h1>
      <Button asChild className="mt-6 rounded-full bg-emerald text-white"><Link to="/directory">Back to Directory</Link></Button>
    </div>
  );
  if (!biz) return <div className="py-32 text-center text-muted-foreground">Loading…</div>;

  return (
    <div className="bg-ivory">
      <div className="bg-navy text-ivory">
        <div className="mx-auto max-w-5xl px-5 lg:px-8 py-14">
          <Link to="/directory" className="inline-flex items-center gap-2 text-sm text-ivory/70 hover:text-gold mb-6"><ArrowLeft className="h-4 w-4" /> All businesses</Link>
          <div className="flex items-start gap-5">
            <div className="h-20 w-20 rounded-xl bg-white/10 flex items-center justify-center overflow-hidden shrink-0">
              {biz.logo_url ? <img src={fileUrl(biz.logo_url)} alt="" className="h-full w-full object-cover" /> : <span className="font-display text-3xl text-gold">{biz.name.replace(/^\[DEMO\]\s*/, "").charAt(0)}</span>}
            </div>
            <div>
              <div className="flex gap-2 mb-2 flex-wrap">
                <Badge className="bg-gold text-navy border-none">{biz.category}</Badge>
                {biz.is_demo && <Badge className="bg-white/15 text-ivory border-none">Demo listing</Badge>}
              </div>
              <h1 className="font-display text-4xl lg:text-5xl font-semibold tracking-tight">{biz.name}</h1>
              <p className="mt-3 flex items-center gap-1.5 text-ivory/70 text-sm"><MapPin className="h-4 w-4" /> {biz.city}, {biz.state} {biz.service_areas?.length > 0 && `· ${biz.service_areas.map((s) => AREA_LABELS[s]).join(", ")}`}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-5xl px-5 lg:px-8 py-14 grid gap-10 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-8">
          <Reveal>
            <h2 className="font-display text-2xl text-navy font-semibold">About</h2>
            <p className="mt-3 text-muted-foreground leading-relaxed whitespace-pre-line">{biz.description}</p>
          </Reveal>
          {biz.faith_statement && (
            <Reveal>
              <div className="rounded-xl border border-emerald/20 bg-emerald/5 p-6">
                <p className="text-xs font-semibold uppercase tracking-wider text-emerald">Owner-submitted statement of faith</p>
                <p className="mt-2 text-navy leading-relaxed whitespace-pre-line">{biz.faith_statement}</p>
              </div>
            </Reveal>
          )}
          {biz.community_involvement && (
            <Reveal>
              <div className="rounded-xl border border-border bg-white p-6">
                <p className="text-xs font-semibold uppercase tracking-wider text-gold-dark">Owner-submitted: community & ministry involvement</p>
                <p className="mt-2 text-muted-foreground leading-relaxed whitespace-pre-line">{biz.community_involvement}</p>
              </div>
            </Reveal>
          )}
          {biz.photos?.length > 0 && (
            <div className="grid grid-cols-2 gap-4">
              {biz.photos.map((p, i) => <img key={i} src={fileUrl(p)} alt="" className="rounded-lg w-full h-48 object-cover" />)}
            </div>
          )}
        </div>

        <aside className="space-y-5">
          <div className="rounded-xl border border-border bg-white p-6 shadow-sm">
            <h3 className="font-display text-xl text-navy font-semibold">Contact & links</h3>
            <div className="mt-4 space-y-3 text-sm">
              {biz.website && <a href={biz.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-emerald hover:underline" data-testid="business-website"><Globe className="h-4 w-4" /> Visit website</a>}
              {biz.public_phone && <a href={`tel:${biz.public_phone}`} className="flex items-center gap-2 text-navy hover:text-emerald"><Phone className="h-4 w-4" /> {biz.public_phone}</a>}
              {biz.public_email && <a href={`mailto:${biz.public_email}`} className="flex items-center gap-2 text-navy hover:text-emerald break-all"><Mail className="h-4 w-4" /> {biz.public_email}</a>}
              {biz.social_links && Object.entries(biz.social_links).filter(([, v]) => v).map(([k, v]) => {
                const Icon = SOCIAL_ICONS[k] || Globe;
                return <a key={k} href={v} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-navy hover:text-emerald capitalize"><Icon className="h-4 w-4" /> {k}</a>;
              })}
              {!biz.website && !biz.public_phone && !biz.public_email && <p className="text-muted-foreground">No public contact details provided.</p>}
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <UpdateDialog id={biz.id} />
            <ReportDialog id={biz.id} />
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed">
            Inclusion in this directory is not a guarantee of services or an endorsement of every
            business practice. Faith and involvement statements are provided by the business owner.
          </p>
        </aside>
      </div>
    </div>
  );
}
